import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Mail, Lock, ShieldCheck, Building2, MessageSquare, Image as ImageIcon, UploadCloud } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr } from "@/lib/api";

export default function AccountSettings() {
  const { user, workspace, refreshMe, changeEmail, changePassword } = useAuth();
  const isOwner = user?.role === "owner";
  const [company, setCompany] = useState("");
  const [savingCompany, setSavingCompany] = useState(false);
  useEffect(() => { if (workspace?.name) setCompany(workspace.name); }, [workspace?.name]);
  const [emailForm, setEmailForm] = useState({ new_email: "", current_password: "" });
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  const submitEmail = async (e) => {
    e.preventDefault();
    setSavingEmail(true);
    try {
      await changeEmail(emailForm.new_email.trim(), emailForm.current_password);
      toast.success("Email updated");
      setEmailForm({ new_email: "", current_password: "" });
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    setSavingEmail(false);
  };

  const submitPw = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) { toast.error("New passwords do not match"); return; }
    if (pwForm.new_password.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    setSavingPw(true);
    try {
      await changePassword(pwForm.current_password, pwForm.new_password);
      toast.success("Password updated");
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    setSavingPw(false);
  };

  const submitCompany = async (e) => {
    e.preventDefault();
    if (!company.trim()) { toast.error("Company name is required"); return; }
    setSavingCompany(true);
    try {
      await api.patch("/auth/workspace", { name: company.trim() });
      await refreshMe();
      toast.success("Company name updated");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    setSavingCompany(false);
  };

  const inputCls = "w-full rounded-lg border border-slate-700 bg-[#0d1420] px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-amber-500 focus:outline-none";
  const labelCls = "mb-1 block font-mono-tac text-[10px] uppercase tracking-widest text-slate-500";

  return (
    <div className="mx-auto max-w-3xl p-5" data-testid="account-settings">
      <p className="font-mono-tac text-[11px] uppercase tracking-widest text-amber-500">Account</p>
      <h1 className="mb-1 font-display text-3xl font-bold text-white">Login &amp; security</h1>
      <p className="mb-8 text-sm text-slate-400">Update the email and password you use to sign in. Signed in as <span className="text-slate-200">{user?.email}</span>.</p>

      {isOwner && (
        <form onSubmit={submitCompany} className="card-tac mb-6 p-5" data-testid="company-settings-form">
          <div className="mb-4 flex items-center gap-2 text-white"><Building2 className="h-4 w-4 text-[#2E7BE4]" /> <span className="font-semibold">Company / workspace</span></div>
          <label className={labelCls}>Business name (shown on quotes &amp; invoices)</label>
          <input data-testid="company-name-input" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Set Up Home Theater LLC" className={`mb-4 ${inputCls}`} />
          <button data-testid="save-company-btn" type="submit" disabled={savingCompany} className="btn-amber rounded-full px-6 py-2 text-sm disabled:opacity-50">{savingCompany ? "Saving…" : "Save company name"}</button>
        </form>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {/* Change email */}
        <form onSubmit={submitEmail} className="card-tac p-5" data-testid="change-email-form">
          <div className="mb-4 flex items-center gap-2 text-white"><Mail className="h-4 w-4 text-cyan-400" /> <span className="font-semibold">Change email</span></div>
          <label className={labelCls}>New email</label>
          <input data-testid="new-email-input" type="email" required value={emailForm.new_email}
            onChange={(e) => setEmailForm((f) => ({ ...f, new_email: e.target.value }))} placeholder="you@company.com" className={`mb-3 ${inputCls}`} />
          <label className={labelCls}>Current password</label>
          <input data-testid="email-current-password-input" type="password" required value={emailForm.current_password}
            onChange={(e) => setEmailForm((f) => ({ ...f, current_password: e.target.value }))} placeholder="••••••••" className={`mb-4 ${inputCls}`} />
          <button data-testid="save-email-btn" type="submit" disabled={savingEmail} className="btn-amber w-full rounded-full py-2 text-sm disabled:opacity-50">{savingEmail ? "Saving…" : "Update email"}</button>
        </form>

        {/* Change password */}
        <form onSubmit={submitPw} className="card-tac p-5" data-testid="change-password-form">
          <div className="mb-4 flex items-center gap-2 text-white"><Lock className="h-4 w-4 text-emerald-400" /> <span className="font-semibold">Change password</span></div>
          <label className={labelCls}>Current password</label>
          <input data-testid="current-password-input" type="password" required value={pwForm.current_password}
            onChange={(e) => setPwForm((f) => ({ ...f, current_password: e.target.value }))} placeholder="••••••••" className={`mb-3 ${inputCls}`} />
          <label className={labelCls}>New password</label>
          <input data-testid="new-password-input" type="password" required value={pwForm.new_password}
            onChange={(e) => setPwForm((f) => ({ ...f, new_password: e.target.value }))} placeholder="At least 8 characters" className={`mb-3 ${inputCls}`} />
          <label className={labelCls}>Confirm new password</label>
          <input data-testid="confirm-password-input" type="password" required value={pwForm.confirm}
            onChange={(e) => setPwForm((f) => ({ ...f, confirm: e.target.value }))} placeholder="Repeat new password" className={`mb-4 ${inputCls}`} />
          <button data-testid="save-password-btn" type="submit" disabled={savingPw} className="btn-amber w-full rounded-full py-2 text-sm disabled:opacity-50">{savingPw ? "Saving…" : "Update password"}</button>
        </form>
      </div>

      <div className="mt-6 flex items-start gap-2 rounded-lg border border-slate-800 bg-[#0d1420] p-4 text-xs text-slate-400">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
        <span>For your security, both changes require your current password. Updating either one refreshes your active session automatically.</span>
      </div>

      {isOwner && <BrandingSettings />}
      {isOwner && <TwilioSettings />}
    </div>
  );
}

function BrandingSettings() {
  const [b, setB] = useState({ logo: null, cover: null });
  const [busy, setBusy] = useState("");
  useEffect(() => { api.get("/workspace/branding").then((r) => setB(r.data)).catch(() => {}); }, []);
  const downscale = (file, maxW, type) => new Promise((res) => {
    const img = new Image(); const url = URL.createObjectURL(file);
    img.onload = () => {
      const s = Math.min(1, maxW / img.width);
      const w = Math.round(img.width * s), h = Math.round(img.height * s);
      const c = document.createElement("canvas"); c.width = w; c.height = h;
      c.getContext("2d").drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(url); c.toBlob((bl) => res(bl), type, 0.88);
    };
    img.src = url;
  });
  const upload = async (kind, file) => {
    if (!file) return; setBusy(kind);
    try {
      const isLogo = kind === "logo";
      const blob = await downscale(file, isLogo ? 400 : 1600, isLogo ? "image/png" : "image/jpeg");
      const fd = new FormData(); fd.append(kind, blob, `${kind}.${isLogo ? "png" : "jpg"}`);
      const { data } = await api.put("/workspace/branding", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setB(data); toast.success(`${isLogo ? "Logo" : "Cover"} updated`);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy("");
  };
  const Tile = ({ kind, label, hint }) => (
    <div className="rounded-xl border border-slate-800 bg-[#0b0f17] p-3">
      <div className="mb-2 font-mono-tac text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      {b[kind]
        ? <img src={b[kind]} alt={label} data-testid={`branding-${kind}-preview`} className={`mb-2 w-full rounded-lg object-contain ${kind === "cover" ? "h-24" : "h-20 bg-white/5 p-2"}`} />
        : <div className={`mb-2 grid w-full place-items-center rounded-lg border border-dashed border-slate-700 text-slate-600 ${kind === "cover" ? "h-24" : "h-20"}`}><ImageIcon className="h-5 w-5" /></div>}
      <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:border-amber-500">
        <UploadCloud className="h-3.5 w-3.5" /> {busy === kind ? "Uploading…" : b[kind] ? "Replace" : "Upload"}
        <input data-testid={`branding-${kind}-input`} type="file" accept="image/*" className="hidden" disabled={busy === kind} onChange={(e) => upload(kind, e.target.files?.[0])} />
      </label>
      <p className="mt-1 text-[10px] text-slate-600">{hint}</p>
    </div>
  );
  return (
    <div className="mt-6 rounded-2xl border border-slate-800 bg-[#0d1420] p-5" data-testid="branding-settings">
      <div className="mb-2 flex items-center gap-2 text-white"><ImageIcon className="h-4 w-4 text-cyan-400" /> <span className="font-semibold">Public timeline branding</span></div>
      <p className="mb-4 text-xs text-slate-400">Your logo and a hero cover appear on shared public timeline links and branded photo reports.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Tile kind="logo" label="Company logo" hint="Square PNG works best (transparent ok)." />
        <Tile kind="cover" label="Timeline cover" hint="Wide hero image, shown behind the title." />
      </div>
    </div>
  );
}

function TwilioSettings() {
  const [f, setF] = useState({ account_sid: "", auth_token: "", from_number: "" });
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/workspace/twilio").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/workspace/twilio", f); toast.success(data.configured ? "Twilio connected - panic texts are now live" : "Twilio settings saved"); setCfg({ ...cfg, ...data }); setF({ account_sid: "", auth_token: "", from_number: f.from_number }); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };
  const inp = "w-full rounded-lg border border-slate-700 bg-[#0b0f17] px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none";
  return (
    <div className="mt-6 rounded-2xl border border-slate-800 bg-[#0d1420] p-5" data-testid="twilio-settings">
      <div className="mb-2 flex items-center gap-2 text-white"><MessageSquare className="h-4 w-4 text-amber-400" /> <span className="font-semibold">SMS text alerts (Twilio)</span>
        {cfg?.configured && <span className="ml-auto rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] text-emerald-300" data-testid="twilio-configured">Connected</span>}
      </div>
      <p className="mb-4 text-xs text-slate-400">Bring your own Twilio account to send real panic &amp; check-in texts to your on-call manager. Twilio bills your account directly. Get these from console.twilio.com.</p>
      <div className="grid gap-3 sm:grid-cols-3">
        <input data-testid="twilio-sid" value={f.account_sid} onChange={(e) => setF({ ...f, account_sid: e.target.value })} placeholder={cfg?.account_sid || "Account SID (AC…)"} className={inp} />
        <input data-testid="twilio-token" value={f.auth_token} onChange={(e) => setF({ ...f, auth_token: e.target.value })} placeholder="Auth Token" type="password" className={inp} />
        <input data-testid="twilio-from" value={f.from_number} onChange={(e) => setF({ ...f, from_number: e.target.value })} placeholder="From +15125551234" className={inp} />
      </div>
      <button data-testid="twilio-save-btn" onClick={save} disabled={busy} className="btn-amber mt-3 rounded-full px-4 py-2 text-xs disabled:opacity-50">{busy ? "Saving…" : "Save Twilio keys"}</button>
    </div>
  );
}
